import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import {getAuthorizedStatus} from '../../app/store/reducers/session';
import {useLazyGetSessionInfoQuery, useLazyGetSsoSessionInfoQuery} from '../../features/loyaltyCloudApi/session';
import {getTokenVerificationLoadingStatus} from '../../app/store/reducers/loading';
import LoadingBox from '../../commons/components/@extended/LoadingBox';
import {getCurrentToken, initializeToken} from '../../commons/utils/cookies';

const TokenVerification = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const {search} = useLocation();
    
    // Move all hooks to the top - they must be called in the same order every time
    const isAuthorized = useSelector(getAuthorizedStatus);
    const tokenStatus = useSelector(getTokenVerificationLoadingStatus);
    const [verifyToken] = useLazyGetSessionInfoQuery();
    const [verifySsoToken] = useLazyGetSsoSessionInfoQuery();
    const [isVerifying, setIsVerifying] = useState(false);
    
    // Add error handling for URLSearchParams
    let newParams;
    let token;
    let urlParseError = null;
    
    try {
        console.log('TokenVerification: Parsing URL search params:', search);
        newParams = new URLSearchParams(search);
        token = newParams.get('token');
        console.log('TokenVerification: Extracted token:', token ? `${token.substring(0, 10)}...` : 'null');
    } catch (error) {
        console.error('TokenVerification: Error parsing URL parameters:', error);
        console.error('TokenVerification: Problematic search string:', search);
        console.error('TokenVerification: Location object:', location);
        urlParseError = error;
    }

    // Handle URL parsing errors
    useEffect(() => {
        if (urlParseError) {
            console.log('TokenVerification: Navigating to login due to URL parsing error');
            navigate('/login');
        }
    }, [urlParseError, navigate]);

    useEffect(() => {
        const verifyTokenAndInitialize = async () => {
            const isRegularLogin = location.pathname === '/verify_login';
            const isSsoLogin = location.pathname === '/verify_sso_login';

            console.log('TokenVerification: Starting verification process', {
                token: token ? `${token.substring(0, 10)}...` : 'null',
                pathname: location.pathname,
                isRegularLogin,
                isSsoLogin,
                tokenStatus: {
                    isLoading: tokenStatus.isLoading,
                    isError: tokenStatus.isError,
                    isSuccess: tokenStatus.isSuccess
                }
            });

            if (token && !urlParseError && !tokenStatus.isLoading && !tokenStatus.isError && !tokenStatus.isSuccess) {
                try {
                    console.log('TokenVerification: Starting token verification...');
                    
                    if (window.heap) {
                        window.heap.track('Auth Verification Started', {
                            authType: isRegularLogin ? 'regular' : isSsoLogin ? 'sso' : 'unknown',
                            path: location.pathname
                        });
                    }

                    let verificationResult;
                    if (isRegularLogin) {
                        console.log('TokenVerification: Verifying regular login token');
                        verificationResult = await verifyToken(token).unwrap();
                    } else if (isSsoLogin) {
                        console.log('TokenVerification: Verifying SSO login token');
                        verificationResult = await verifySsoToken(token).unwrap();
                    }
                    
                    console.log('TokenVerification: Token verification successful', verificationResult);
                    console.log('TokenVerification: Initializing token...');
                    await initializeToken();

                    if (window.heap) {
                        window.heap.track('Auth Verification Success', {
                            authType: isRegularLogin ? 'regular' : isSsoLogin ? 'sso' : 'unknown'
                        });
                    }

                    console.log('TokenVerification: Navigation to home page');
                    navigate('/');
                } catch (error) {
                    console.error('TokenVerification: Token verification failed:', error);
                    console.error('TokenVerification: Error details:', {
                        message: error.message,
                        code: error.code,
                        status: error.status,
                        data: error.data,
                        stack: error.stack
                    });
                    console.error('TokenVerification: Full error object:', error);
                    
                    if (window.heap) {
                        window.heap.track('Auth Verification Failed', {
                            authType: isRegularLogin ? 'regular' : isSsoLogin ? 'sso' : 'unknown',
                            errorMessage: error.message || 'Unknown error',
                            errorCode: error.code || 'unknown',
                            errorStatus: error.status || 'unknown'
                        });
                    }

                    console.log('TokenVerification: Navigating to login page due to error');
                    navigate('/login');
                } finally {
                    console.log('TokenVerification: Setting isVerifying to false');
                    setIsVerifying(false);
                }
            } else {
                console.log('TokenVerification: Skipping verification due to conditions:', {
                    hasToken: !!token,
                    isLoading: tokenStatus.isLoading,
                    isError: tokenStatus.isError,
                    isSuccess: tokenStatus.isSuccess
                });
            }
        };

        verifyTokenAndInitialize();
    }, [token, verifyToken, verifySsoToken, location.pathname, tokenStatus, navigate, isVerifying]);

    useEffect(() => {
        const checkAuthorization = async () => {
            console.log('TokenVerification: Checking authorization', {
                isVerifying,
                isAuthorized,
                tokenStatus: {
                    isError: tokenStatus.isError,
                    isLoading: tokenStatus.isLoading,
                    isSuccess: tokenStatus.isSuccess
                },
                hasToken: !!token
            });

            if (!isVerifying) {
                try {
                    const currentToken = await getCurrentToken();
                    console.log('TokenVerification: Current token status:', {
                        hasCurrentToken: !!currentToken,
                        currentTokenPreview: currentToken ? `${currentToken.substring(0, 10)}...` : 'null'
                    });

                    if (isAuthorized && currentToken) {
                        console.log('TokenVerification: User is authorized, navigating to home');
                        navigate('/');
                    } else if (tokenStatus.isError || (!isAuthorized && !currentToken && !token)) {
                        console.log('TokenVerification: Authorization failed, navigating to login', {
                            tokenStatusError: tokenStatus.isError,
                            notAuthorized: !isAuthorized,
                            noCurrentToken: !currentToken,
                            noToken: !token
                        });
                        navigate('/login');
                    }
                } catch (error) {
                    console.error('TokenVerification: Error checking authorization:', error);
                    console.log('TokenVerification: Navigating to login due to authorization check error');
                    navigate('/login');
                }
            }
        };

        checkAuthorization();
    }, [isAuthorized, navigate, tokenStatus.isError, isVerifying, token]);

    return <LoadingBox />;
};

export default TokenVerification;
